'use client'

import Link from 'next/link'
import { type KeyboardEvent, useState } from 'react'

import { FAQ_ITEMS } from '@/lib/faq'

const pad = (n: number) => String(n).padStart(2, '0')
const TOTAL = pad(FAQ_ITEMS.length)

/**
 * FAQ as the comp draws it: the questions as a list on the left, the chosen
 * answer in a glass panel on the right. Hover or click picks; arrow keys move
 * through the list. The same `FAQ_ITEMS` feed the page's FAQPage JSON-LD, so
 * what Google reads is what a visitor can open.
 */
export function HomeFaq({ locale }: { locale: string }) {
  const [open, setOpen] = useState(0)
  const item = FAQ_ITEMS[open] ?? FAQ_ITEMS[0]

  const onKey = (event: KeyboardEvent) => {
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    event.preventDefault()
    const next = (open + step + FAQ_ITEMS.length) % FAQ_ITEMS.length
    setOpen(next)
    document.getElementById(`fh-faq-tab-${next}`)?.focus()
  }

  return (
    <section
      id="faq"
      aria-labelledby="faq-d"
      className="fh-wrap"
      style={{ marginTop: 160, display: 'flex', flexDirection: 'column', gap: 40 }}
    >
      <div
        className="fh-rv"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p className="fh-kicker" style={{ margin: 0 }}>
            FAQ
          </p>
          <h2
            id="faq-d"
            className="fh-m fh-h2"
            style={{ margin: 0, fontSize: 'clamp(2.6rem, 4vw, 4.4rem)', lineHeight: 1 }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>Wszystko, co warto wiedzieć</span>
            <br />
            <span style={{ color: 'var(--muted)' }}>przed startem.</span>
          </h2>
        </div>
        <p style={{ margin: 0, fontSize: '1rem', lineHeight: 1.6, color: 'var(--muted)' }}>
          Nie ma tu Twojego pytania?{' '}
          <a href="mailto:kontakt@myflou.pl" className="fh-ul-link">
            kontakt@myflou.pl
          </a>
        </p>
      </div>
      <div className="fh-g2 fh-fx fh-rv">
        <div className="fh-fx-list" role="tablist" aria-label="Pytania" aria-orientation="vertical">
          {FAQ_ITEMS.map((faq, k) => {
            const on = k === open
            return (
              <button
                key={faq.question}
                id={`fh-faq-tab-${k}`}
                type="button"
                role="tab"
                aria-selected={on}
                aria-controls="fh-faq-panel"
                tabIndex={on ? 0 : -1}
                className={on ? 'fh-fx-tab fh-on' : 'fh-fx-tab'}
                onClick={() => setOpen(k)}
                onMouseEnter={() => setOpen(k)}
                onKeyDown={onKey}
              >
                <span className="fh-fx-n">{pad(k + 1)}</span>
                <span className="fh-fx-q">{faq.question}</span>
                <svg className="fh-ico fh-fx-arr" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </button>
            )
          })}
        </div>
        <div
          id="fh-faq-panel"
          className="fh-fx-panel"
          role="tabpanel"
          aria-labelledby={`fh-faq-tab-${open}`}
        >
          <div className="fh-fx-swap" key={open}>
            <p className="fh-fx-count">
              {pad(open + 1)} <span>/ {TOTAL}</span>
            </p>
            <h3 className="fh-m fh-fx-title">{item?.question}</h3>
            <p className="fh-fx-ans">{item?.answer}</p>
          </div>
          <div className="fh-fx-foot">
            <span>Chcesz porozmawiać o swoim projekcie?</span>
            <Link
              href={`/${locale}/strategia`}
              className="fh-btn fh-btn-ink"
              style={{ minHeight: 46 }}
            >
              Umów konsultację
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}
